// src/components/context/CalendarContext.js
import React, { createContext, useState, useCallback, useRef } from 'react';
import api from '../../config/axiosInstance';

export const CalendarContext = createContext();

const normalizeValidationBucket = (bucketOrMessages, fallbackKind) => {
  if (Array.isArray(bucketOrMessages)) {
    return bucketOrMessages.map((message) => ({
      message: String(message),
      scope: { kind: fallbackKind },
    }));
  }

  const items = bucketOrMessages?.items;
  if (Array.isArray(items) && items.length > 0) {
    return items.map((item) => ({
      message: String(item?.message ?? ''),
      scope: item?.scope || { kind: fallbackKind },
    }));
  }

  const messages = bucketOrMessages?.messages;
  if (Array.isArray(messages)) {
    return messages.map((message) => ({
      message: String(message),
      scope: { kind: fallbackKind },
    }));
  }

  return [];
};

const dedupeValidationItems = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const key = JSON.stringify({
      message: item?.message ?? '',
      scope: item?.scope || {},
    });
    map.set(key, item);
  });
  return Array.from(map.values());
};

const scopeMatchesFilter = (scope = {}, filter = {}) => {
  if (!filter || Object.keys(filter).length === 0) return false;
  return Object.entries(filter).every(([key, value]) => scope?.[key] === value);
};

const overtimeKey = (ci, day) => `${ci}__${String(day).slice(0, 10)}`;

export const CalendarProvider = ({ children }) => {
  const [calendarData, setCalendarData] = useState(null);
  const [calendarErrorItems, setCalendarErrorItemsState] = useState([]);
  const [calendarWarningItems, setCalendarWarningItemsState] = useState([]);
  const [calendarExists, setCalendarExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overtimeMap, setOvertimeMap] = useState({});
  const validationRequestRef = useRef(0);

  const fetchOvertimeRange = useCallback(async ({ station, date_from, date_to }) => {
    if (!station || !date_from || !date_to) return;
    try {
      const { data } = await api.get('calendar/overtime_hours', {
        params: { station_id: station, date_from, date_to },
      });
      const map = {};
      (data?.items || []).forEach((item) => {
        const key = overtimeKey(item.ci, item.date);
        if (!map[key]) map[key] = [];
        map[key].push(item);
      });
      setOvertimeMap(map);
    } catch (error) {
      console.error('Error al obtener horas extra:', error);
    }
  }, []);

  const setOvertimeForDay = useCallback((ci, day, items) => {
    setOvertimeMap((prev) => ({ ...prev, [overtimeKey(ci, day)]: items }));
  }, []);

  const getOvertimeForDay = useCallback(
    (ci, day) => overtimeMap[overtimeKey(ci, day)] || [],
    [overtimeMap]
  );

  const calendarErrors = calendarErrorItems.map((item) => item.message);
  const calendarWarnings = calendarWarningItems.map((item) => item.message);

  const setCalendarErrors = useCallback((bucketOrMessages) => {
    setCalendarErrorItemsState(normalizeValidationBucket(bucketOrMessages, 'manual'));
  }, []);

  const setCalendarWarnings = useCallback((bucketOrMessages) => {
    setCalendarWarningItemsState(normalizeValidationBucket(bucketOrMessages, 'manual'));
  }, []);

  const replaceCalendarValidation = useCallback(({ errors, warnings }) => {
    setCalendarErrorItemsState(normalizeValidationBucket(errors, 'full'));
    setCalendarWarningItemsState(normalizeValidationBucket(warnings, 'full'));
  }, []);

  const mergeCalendarValidationPatch = useCallback(({ errors, warnings, replaceScopes = [] }) => {
    const nextErrorItems = normalizeValidationBucket(errors, 'patch');
    const nextWarningItems = normalizeValidationBucket(warnings, 'patch');

    setCalendarErrorItemsState((prev) => {
      const filtered = prev.filter(
        (item) => !replaceScopes.some((scope) => scopeMatchesFilter(item.scope, scope))
      );
      return dedupeValidationItems([...filtered, ...nextErrorItems]);
    });

    setCalendarWarningItemsState((prev) => {
      const filtered = prev.filter(
        (item) => !replaceScopes.some((scope) => scopeMatchesFilter(item.scope, scope))
      );
      return dedupeValidationItems([...filtered, ...nextWarningItems]);
    });
  }, []);

  /**
   * Refresca el calendario desde backend.
   * @param {Object} options - { date, station, position }
   */
  const fetchCalendar = useCallback(async ({ date, station, position, loadValidation = true }) => {

   // console.log('%c[fetchCalendar] date ➞', 'color: blue; font-weight: bold;', date);

    if (!date || !station || !position) return null;
  
    try {
      if (!loadValidation) {
        // Invalida una validación anterior si el usuario cambió de período.
        validationRequestRef.current += 1;
      }
      setLoading(true);
  
      const response = await api.get('calendar/get', {
        params: { date, station, position, include_validation: loadValidation }
      });
  
      const exists = response.data.exists;
      const calendar = response.data.calendar_data || response.data.updated_calendar;
  
      setCalendarExists(!!exists);

      setCalendarData(calendar);
      if (loadValidation) {
        replaceCalendarValidation({
          errors: response.data.errors,
          warnings: response.data.warnings,
        });
      }

      if (calendar?.station && calendar?.start_date && calendar?.end_date) {
        fetchOvertimeRange({
          station: calendar.station,
          date_from: calendar.start_date,
          date_to: calendar.end_date,
        });
      } else {
        setOvertimeMap({});
      }

      // console.log('Errores nuevos:', response.data.errors?.messages);
      // console.log('Advertencias nuevas:', response.data.warnings?.messages);
  
      return calendar; // Por si se necesita usar el dato luego


    } catch (error) {
      console.error('Error al obtener calendario:', error);
      setCalendarExists(false);
      setCalendarData(null);
      setCalendarErrors([error.message || 'Error al obtener calendario']);
      setCalendarWarnings([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, [replaceCalendarValidation, setCalendarErrors, setCalendarWarnings, fetchOvertimeRange]);

  const fetchCalendarValidation = useCallback(async ({ date, station, position }) => {
    if (!date || !station || !position) return null;

    const requestId = ++validationRequestRef.current;

    try {
      const response = await api.get('calendar/validation', {
        params: { date, station, position },
      });

      if (requestId === validationRequestRef.current) {
        replaceCalendarValidation({
          errors: response.data.errors,
          warnings: response.data.warnings,
        });
      }

      return response.data;
    } catch (error) {
      console.error('Error al validar calendario:', error);
      return null;
    }
  }, [replaceCalendarValidation]);
  


  return (
    <CalendarContext.Provider
      value={{
        calendarData,
        setCalendarData,
        calendarErrors,
        setCalendarErrors,
        calendarWarnings,
        setCalendarWarnings,
        calendarErrorItems,
        calendarWarningItems,
        replaceCalendarValidation,
        mergeCalendarValidationPatch,
        calendarExists, 
        setCalendarExists,
        loading,
        fetchCalendar,
        fetchCalendarValidation,
        overtimeMap,
        fetchOvertimeRange,
        setOvertimeForDay,
        getOvertimeForDay,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};
